// @ts-nocheck
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { Viewer } from "@speckle/viewer";
import * as Sentry from "@sentry/vue";
import { blockPicking } from "@/stores/speckle/pick-guard";

import { useSpeckleStore } from "@/stores/speckle/speckle.store";
import { getProjectMaterials } from "@/api/services/Mapping";
import { excludedIdsToPaint } from "@/stores/speckle/excludedOverlay";

type Mode = "auto" | "type" | "material";

export const useSpeckleFilters = defineStore("speckleFilters", () => {
  const core = useSpeckleStore();

  const indexesBuilt = ref(false);
  const lastMatchedIds = ref<string[]>([]);
  // The last user-object colour groups painted (by any overlay). Cached so reapplyColors() can
  // restore them after a ViewMode switch, which can override user colours at the renderer level.
  let lastColorGroups: { objectIds: string[]; color: string }[] = [];
  let focusedObjectIds = new Set<string>();

  // Shared emission-range filter. null = no active range (full extent). Set by EmissionHeatmap,
  // read by DataQualityStatus so both panels respect the same narrowed range.
  const emissionRange = ref<{ min: number; max: number } | null>(null);

  // Which (type, material) pairs are excluded (`materialLabel: null` = the whole type), painted grey
  // no matter which tool panel is open. Data-level; survives a viewer re-init, unlike the scene-specific ids below.
  const excludedPairs = ref<{ typeLabel: string; materialLabel: string | null }[]>([]);
  const excludedObjectIds = ref<string[]>([]);
  const EXCLUDED_GREY = "#94a3b8"; // slate-400

  const setEmissionRange = (range: { min: number; max: number } | null) => {
    emissionRange.value = range;
  };

  const getSelecting = () => {
    return (core as any).selectRef?.value ?? (core as any).selectRef ?? null;
  };

  // indices & maps (rebuilt on invalidate)
  const objToRoot = new Map<string, string>(); // objectId -> rootId
  const materialIndex = new Map<string, Set<string>>(); // normalized label -> objectIds
  const typeIndex = new Map<string, Set<string>>(); // normalized label -> objectIds
  const categoryIndex = new Map<string, Set<string>>(); // normalized category -> objectIds
  const categoryNames = new Map<string, string>(); // normalized category -> display name
  const familyIndex = new Map<string, Set<string>>(); // normalized family -> objectIds
  const familyNames = new Map<string, string>(); // normalized family -> display name

  const norm = (s?: string) =>
    (s ?? "")
      .toLocaleLowerCase("da")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .trim();

  function collectMaterialKeys(raw: any): string[] {
    const out: string[] = [];

    if (!raw) return out;

    // 1) From materialQuantities
    for (const mq of raw?.materialQuantities ?? []) {
      const mat = mq?.material ?? {};
      const name = mat?.name;
      const id = mat?.id;
      const ref = mat?.referencedId;
      if (name) out.push(name);
      else if (id) out.push(id);
      else if (ref) out.push(ref);
    }

    // 2) From parameters that look material-related
    const params = raw?.parameters ?? {};
    for (const [k, v] of Object.entries(params)) {
      if (typeof k !== "string") continue;
      const keyLower = k.toLowerCase();
      if (keyLower.includes("material") || keyLower.includes("type") || keyLower.includes("name")) {
        const val = (v as any)?.value ?? (v as any);
        if (val && typeof val === "string") out.push(val);
      }
    }

    // 3) From type/family/category fields (these might contain compound labels like "Floor:Concrete")
    if (typeof raw?.type === "string") out.push(raw.type);
    if (typeof raw?.family === "string") out.push(raw.family);
    if (typeof raw?.category === "string") out.push(raw.category);
    if (typeof raw?.name === "string") out.push(raw.name);

    // 4) Try to construct compound labels from type + name or similar combinations
    const typeStr = typeof raw?.type === "string" ? raw.type : "";
    const nameStr = typeof raw?.name === "string" ? raw.name : "";
    const familyStr = typeof raw?.family === "string" ? raw.family : "";

    // Combine type with name if both exist (e.g., "Floor:Concrete")
    if (typeStr && nameStr && typeStr !== nameStr) {
      out.push(`${typeStr}:${nameStr}`);
    }
    if (typeStr && familyStr && typeStr !== familyStr) {
      out.push(`${typeStr}:${familyStr}`);
    }

    // 5) Check for any fields that might contain the label directly
    // Sometimes labels come from displayName, label, or other fields
    if (typeof raw?.displayName === "string") out.push(raw.displayName);
    if (typeof raw?.label === "string") out.push(raw.label);

    // 6) Look for nested objects that might contain material info
    if (raw?.speckle_type) {
      // Sometimes the type is in speckle_type
      const speckleType = String(raw.speckle_type);
      if (speckleType) out.push(speckleType);
    }

    // Return raw strings (will be normalized in buildIndexes)
    const result = Array.from(new Set(out.filter(Boolean)));
    return result;
  }

  function getTypeKey(raw: any): string {
    const p = raw?.parameters ?? {};
    const typeName = p?.["Type Name"]?.value ?? (p as any)?.TYPE_NAME?.value;
    return norm(typeName ?? raw?.type ?? "");
  }

  function buildIndexes() {
    const v = core.viewer;
    if (!v) return;

    objToRoot.clear();
    materialIndex.clear();
    typeIndex.clear();
    categoryIndex.clear();
    categoryNames.clear();
    familyIndex.clear();
    familyNames.clear();

    const wt = v.getWorldTree();
    const renderTreeKeys = new Set(Object.keys((wt as any).renderTreeInstances || {})); // <-- authoritative root keys (referencedObjectId)

    // Helper: climb up and pick the first ancestor whose model.id is a render-tree key
    const rootKeyFor = (node: any): string => {
      let cur = node;
      while (cur) {
        const mid = cur?.model?.id && String(cur.model.id);
        if (mid && renderTreeKeys.has(mid)) return mid; // <- ✅ referencedObjectId
        cur = cur.parent;
      }
      // fallback: single-model scenes or odd trees
      if (renderTreeKeys.size === 1) return Array.from(renderTreeKeys)[0];
      return String(node?.model?.id ?? node?.id ?? "ROOT");
    };

    const nodes = wt.findAll((n: any) => {
      if (n?.model?.raw) return true;
      // climb up to find a raw-bearing ancestor
      let p = n?.parent;
      while (p) {
        if (p?.model?.raw) return true;
        p = p.parent;
      }
      return false;
    });
    for (const n of nodes) {
      const oid = String(n.model?.id ?? n.id);
      if (!oid) continue;

      const rootKey = rootKeyFor(n); // <-- store referencedObjectId here
      objToRoot.set(oid, rootKey);

      // Try to get raw from node or climb up
      let raw = n.model?.raw;
      if (!raw) {
        let p = n?.parent;
        while (p && !raw) {
          raw = p?.model?.raw;
          p = p?.parent;
        }
      }

      if (!raw) continue;

      // materials
      const materialKeys = collectMaterialKeys(raw);
      for (const m of materialKeys) {
        const k = norm(m);
        if (!k) continue;
        if (!materialIndex.has(k)) materialIndex.set(k, new Set());
        materialIndex.get(k)!.add(oid);
      }

      // type
      const tKey = norm(getTypeKey(raw));
      if (tKey) {
        if (!typeIndex.has(tKey)) typeIndex.set(tKey, new Set());
        typeIndex.get(tKey)!.add(oid);
      }

      // category (direct property on the element — drives the Inspect category filter)
      const cat = typeof raw?.category === "string" ? raw.category : "";
      const catKey = norm(cat);
      if (catKey) {
        if (!categoryIndex.has(catKey)) {
          categoryIndex.set(catKey, new Set());
          categoryNames.set(catKey, cat);
        }
        categoryIndex.get(catKey)!.add(oid);
      }

      // family (the Revit family — the parent grouping between category and type)
      const fam = typeof raw?.family === "string" ? raw.family : "";
      const famKey = norm(fam);
      if (famKey) {
        if (!familyIndex.has(famKey)) {
          familyIndex.set(famKey, new Set());
          familyNames.set(famKey, fam);
        }
        familyIndex.get(famKey)!.add(oid);
      }
    }

    indexesBuilt.value = true;
  }

  // The query was already guarded, but a one-character index key (a type/material literally named "S")
  // satisfied q.includes(k) for nearly every query. Exact lookups are unaffected by this minimum.
  const MIN_FUZZY_LENGTH = 2;
  const fuzzyHit = (key: string, query: string) =>
    key.length >= MIN_FUZZY_LENGTH && query.length >= MIN_FUZZY_LENGTH && (key.includes(query) || query.includes(key));

  function getIdsForLabel(label: string, mode: Mode = "auto"): string[] {
    const q = norm(label);
    const out = new Set<string>();

    if (mode !== "type") {
      const exact = materialIndex.get(q);
      if (exact) {
        exact.forEach((id) => out.add(String(id)));
      }
      if (!out.size) {
        // Try substring match (bidirectional)
        // Guard k.includes(q) on length: a 1-char label like "s" otherwise matches nearly every key.
        for (const [k, set] of materialIndex) {
          if (fuzzyHit(k, q)) {
            set.forEach((id) => out.add(String(id)));
          }
        }
      }
    }

    if (!out.size && mode !== "material") {
      const exact = typeIndex.get(q);
      if (exact) {
        exact.forEach((id) => out.add(String(id)));
      }
      // "type" mode carries an exact, known Revit type identifier, so it must match the type index exactly —
      // fuzzy substring matching would bleed one type's highlight into unrelated types.
      if (!out.size && mode !== "type") {
        // Try substring match (bidirectional)
        // Guard k.includes(q) on length: a 1-char label like "s" otherwise matches nearly every key.
        for (const [k, set] of typeIndex) {
          if (fuzzyHit(k, q)) {
            set.forEach((id) => out.add(String(id)));
          }
        }
      }
    }

    // Skipped for "type" mode: a known type must resolve exactly or not at all — token matching here
    // is what lights up unrelated elements (a shared token like "mm" alone matches almost every type).
    if (!out.size && mode !== "type") {
      const parts = q.split(/[:;,\s]+/).filter(Boolean);
      for (const part of parts) {
        if (part.length < 2) continue; // Skip very short parts

        if (mode !== "type") {
          for (const [k, set] of materialIndex) {
            if (fuzzyHit(k, part)) {
              set.forEach((id) => out.add(String(id)));
            }
          }
        }

        if (mode !== "material") {
          for (const [k, set] of typeIndex) {
            if (fuzzyHit(k, part)) {
              set.forEach((id) => out.add(String(id)));
            }
          }
        }
      }
    }

    return [...out];
  }

  // Strict lookup first, fuzzy only as fallback: byType labels are Family:Type while typeIndex is keyed
  // by bare type, so a strict miss is routine, but where strict hits, the fuzzy superset would bleed.
  function resolveIds(label: string, mode: Mode = "type"): string[] {
    const ids = getIdsForLabel(label, mode);
    return ids.length || mode === "auto" ? ids : getIdsForLabel(label, "auto");
  }

  // RVs for all descendants under a model root (your working pattern)
  function rvsUnderRoot(rootId: string): any[] {
    const v = core.viewer!;
    const wt = v.getWorldTree();
    const rt = wt.getRenderTree(rootId);
    const node = wt.findId(rootId)?.[0];
    if (!rt || !node) return [];

    const rvs = rt.getRenderViewsForNode(node) || [];
    if (!rvs.length) {
      const stack = [node];
      while (stack.length) {
        const n = stack.pop()!;
        const views = rt.getRenderViewsForNode(n) || [];
        if (views.length) rvs.push(...views);
        (n.children ?? []).forEach((c: any) => stack.push(c));
      }
    }
    return rvs;
  }

  // RVs for specific object ids (robust)
  function rvsForObjectIds(v: Viewer, objectIds: string[]): any[] {
    const wt = v.getWorldTree();
    const rvs: any[] = [];

    for (const oid of objectIds) {
      const arr = wt.findId(oid) || [];
      const node = Array.isArray(arr) ? arr[0] : arr;
      if (!node) continue;

      const k1 = String(node?.model?.id ?? "");
      const k2 = String(node?.id ?? "");
      let rt = k1 ? wt.getRenderTree(k1) : null;
      if (!rt && k2) rt = wt.getRenderTree(k2);

      let views: any[] = [];
      if (rt) views = rt.getRenderViewsForNode?.(node) || [];
      if (!views.length) {
        const inst = (wt as any).renderTreeInstances || {};
        for (const key of Object.keys(inst)) {
          const cand = wt.getRenderTree(key);
          const maybe = cand?.getRenderViewsForNode?.(node) || [];
          if (maybe.length) {
            views = maybe;
            break;
          }
        }
      }
      if (views.length) rvs.push(...views);
    }

    // de-dupe
    const seen = new Set<string>();
    return rvs.filter((rv: any) => {
      const k = String(rv?.id ?? rv?.uuid ?? rv);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }

  // replace your current getVisibleRoots with the simpler one (you already have this):
  function getVisibleRoots(): string[] {
    const vis: Set<string> | undefined = (core as any).visible?.value ?? (core as any).visible;
    return vis ? Array.from(vis).map(String) : [];
  }

  const clearFilters = () => {
    const v = core.viewer;
    if (!v) return;

    try {
      getSelecting()?.clearSelection?.();
    } catch {
      // intentional
    }

    const roots = getVisibleRoots();
    for (const rootId of roots) {
      const rvs = rvsUnderRoot(rootId);
      if (!rvs.length) continue;
      const renderer = v.getRenderer();
      for (const rv of rvs) {
        renderer.setMaterial([rv], renderer.getBatchMaterial(rv));
      }
    }

    lastMatchedIds.value = [];
    // The direct renderer.setMaterial() reset above bypasses setUserObjectColors, wiping any painted
    // overlay (including the persistent excluded grey) at the renderer level — restore it.
    reapplyColors();
    v.requestRender?.();
  };

  // A new viewer is a new scene: every id this store cached belongs to the previous one. Drop the indexes,
  // the last match, the painted groups and the shared range before anything can zoom to or paint stale ids.
  watch(
    () => core.viewer,
    (v) => {
      resetSceneState();
      if (!v) return;
      blockPicking(v);
    },
    { immediate: true }
  );

  function resetSceneState() {
    invalidate();
    lastMatchedIds.value = [];
    lastColorGroups = [];
    focusedObjectIds = new Set();
    emissionRange.value = null;
    // A new viewer means a new World Tree: yesterday's object ids don't resolve in it. The pairs
    // themselves are reloaded by whoever mounts the new viewer.
    excludedPairs.value = [];
    excludedObjectIds.value = [];
  }

  // Whether any of the ids resolve to geometry in the current scene; a zoom to ids that do not yields an
  // empty bounding box and a camera that never moves.
  function hasRenderableIds(ids: string[]): boolean {
    const v = core.viewer;
    return !!v && ids.length > 0 && rvsForObjectIds(v, ids).length > 0;
  }

  // Its labels come from the dashboard breakdown, which also covers models the viewer hasn't loaded.
  // classifyLabel is not a substitute: it queries the strict modes only.
  function labelResolvesInModel(label: string, mode: Mode = "type"): boolean {
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();
    return hasRenderableIds(resolveIds(label, mode));
  }

  function invalidate() {
    indexesBuilt.value = false;
    objToRoot.clear();
    materialIndex.clear();
    typeIndex.clear();
    categoryIndex.clear();
    categoryNames.clear();
    familyIndex.clear();
    familyNames.clear();
    try {
      getSelecting()?.clearSelection?.();
    } catch {
      // intentional
    }
  }

  /** The object ids the camera should zoom to: whatever the last overlay matched, if anything. */
  function getZoomTargets(): string[] {
    return [...new Set(lastMatchedIds.value.map(String))];
  }

  // Zooms only when ids resolve to renderable geometry (a type can span models not loaded, yielding an
  // empty box). Checked via the world tree directly, not the visible-roots map, whose keys don't line up with objToRoot's.
  function zoomToIds(ids: string[]) {
    const v = core.viewer;
    const cam = core.cameraRef?.value ?? core.cameraRef;
    if (!v || !cam?.setCameraView || !ids.length) return;
    if (rvsForObjectIds(v, ids).length) cam.setCameraView(ids, true);
  }

  // Enables the extension so the selection material renders (the app's "element selection on" state,
  // restored by Section on leave), then selects the exact set; cleared by the store's clearSelection().
  function selectObjectIds(ids: string[]) {
    const v = core.viewer;
    const sel = getSelecting();
    if (!v || !sel || !ids.length) return;
    try {
      sel.enabled = true;
      sel.selectObjects?.(ids.map(String), false);
    } catch {
      // intentional
    }
    v.requestRender?.();
  }

  // The "inspect" key namespaces this isolation so it doesn't collide with the emission/data-quality ones.
  // Returns whether anything was actually isolated, so a failed isolation can't strand the "Show all" button.
  function isolateObjectIds(ids: string[]): boolean {
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    if (!v || !filtering || !ids.length) return false;
    try {
      filtering.resetFilters?.();
    } catch {
      // intentional
    }
    try {
      const state = filtering.isolateObjects?.(ids, "inspect", true, true);
      reapplyColors();
      v.requestRender?.();
      return !!state?.isolatedObjects?.length;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }

  function clearInspectIsolation() {
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    try {
      filtering?.resetFilters?.();
    } catch {
      // intentional
    }
    reapplyColors();
    // Drop the last zoom target so a following recenterView() fits the whole model, not a stale subset.
    lastMatchedIds.value = [];
    v?.requestRender?.();
  }

  // Resolves via getTypeKey — the same key buildIndexes() uses — so it matches even when the Revit
  // "Type Name" parameter differs from raw.type. Lets Inspect select/zoom/isolate the whole type.
  function objectIdsForRawType(raw: Record<string, unknown> | null): string[] {
    if (!raw) return [];
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();
    const set = typeIndex.get(getTypeKey(raw));
    return set ? [...set].map(String) : [];
  }

  // Intersects the type's ids (via resolveIds — byType labels are Family:Type, so the strict-only
  // getIdsForLabel misses routinely) with the material's, falling back to material-only when empty.
  function resolveExcludedIds(typeLabel: string, materialLabel: string | null): string[] {
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();
    const typeIds = resolveIds(typeLabel, "type");
    if (!materialLabel) return typeIds;
    const materialIds = getIdsForLabel(materialLabel, "material");
    if (!typeIds.length) return materialIds;
    const typeIdSet = new Set(typeIds);
    const intersected = materialIds.filter((id) => typeIdSet.has(id));
    return intersected.length ? intersected : materialIds;
  }

  // Re-resolves every excluded pair and repaints, filtering to ids that actually resolve rather than
  // gating all-or-nothing — one id from an unloaded model must not blank every other renderable one.
  function recomputeExcludedOverlay() {
    const all = new Set<string>();
    for (const pair of excludedPairs.value) {
      for (const id of resolveExcludedIds(pair.typeLabel, pair.materialLabel)) all.add(String(id));
    }
    excludedObjectIds.value = [...all].filter((id) => hasRenderableIds([id]));
    reapplyColors();
  }

  // Replaces the whole excluded-pairs set and repaints — used by the initial load and by a full refetch.
  function setExcludedPairs(pairs: { typeLabel: string; materialLabel: string | null }[]) {
    excludedPairs.value = pairs ?? [];
    recomputeExcludedOverlay();
  }

  // Optimistic single-pair update: a viewer-panel toggle calls this right after its write succeeds, so
  // the grey overlay updates instantly instead of waiting on a full materials refetch.
  function setExcludedForPair(typeLabel: string, materialLabel: string, excluded: boolean) {
    const targetKey = `${norm(typeLabel)}::${norm(materialLabel)}`;
    const keyOf = (pair: { typeLabel: string; materialLabel: string | null }) =>
      `${norm(pair.typeLabel)}::${pair.materialLabel === null ? "" : norm(pair.materialLabel)}`;
    const withoutTarget = excludedPairs.value.filter((pair) => keyOf(pair) !== targetKey);
    setExcludedPairs(excluded ? [...withoutTarget, { typeLabel, materialLabel }] : withoutTarget);
  }

  // Loads which (type, material) pairs are excluded, from the same byType tree shape useSpeckleEpdAssign
  // reads. `revisionId` is the caller's own resolved pin — never fetched here (see the caller for why).
  async function loadExcludedLayers(projectId: string, revisionId?: string) {
    if (!projectId) return;
    try {
      const data = (await getProjectMaterials(projectId, true, revisionId)) as {
        materials?: Record<string, { identifier?: string; includeMapping?: boolean; children?: unknown[] }[]>;
      } | null;
      const types = Object.values(data?.materials ?? {}).flat();
      const pairs: { typeLabel: string; materialLabel: string | null }[] = [];
      for (const type of types) {
        if (!type?.identifier) continue;
        if (type.includeMapping === false) {
          pairs.push({ typeLabel: type.identifier, materialLabel: null });
          continue;
        }
        for (const child of (type.children ?? []) as { identifier?: string; includeMapping?: boolean }[]) {
          if (child?.identifier && child.includeMapping === false) {
            pairs.push({ typeLabel: type.identifier, materialLabel: child.identifier });
          }
        }
      }
      setExcludedPairs(pairs);
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  // A model finishing loading can newly resolve pairs that had no geometry yet — repaint whenever the
  // loaded set changes, not just when a toggle happens.
  watch(
    () => core.loaded,
    () => {
      if (excludedPairs.value.length) recomputeExcludedOverlay();
    },
    { deep: true }
  );

  // Paint user object colours and remember them, so a later ViewMode change can re-apply them.
  function applyUserColors(groups: { objectIds: string[]; color: string }[], focusedIds: string[] = []) {
    lastColorGroups = groups;
    focusedObjectIds = new Set(focusedIds.map(String));
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    try {
      filtering?.setUserObjectColors?.(combinedGroups(groups));
    } catch {
      // intentional
    }
  }

  // Appends the excluded grey last so it wins over a tool's colour, except on focused ids (see excludedIdsToPaint).
  function combinedGroups(groups: { objectIds: string[]; color: string }[]) {
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    const excluded = excludedIdsToPaint(excludedObjectIds.value, filtering?.filteringState?.isolatedObjects, focusedObjectIds);
    if (!excluded.length) return groups;
    return [...groups, { objectIds: excluded, color: EXCLUDED_GREY }];
  }

  // Restores the LCA + excluded overlay after a mode switch or resetFilters(). Never early-returns on
  // an empty combined set — un-excluding the last layer must actively clear it, not leave grey stuck.
  function reapplyColors() {
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    const groups = combinedGroups(lastColorGroups);
    try {
      if (groups.length) filtering?.setUserObjectColors?.(groups);
      else filtering?.removeUserObjectColors?.();
    } catch {
      // intentional
    }
    core.viewer?.requestRender?.();
  }

  // Uses the same isolate+colour path as focusDataQualityType, minus the camera move, since a bare
  // setUserObjectColors recolour doesn't reliably re-render; lastColorGroups stays untouched for clearPreview().
  const PREVIEW_HIGHLIGHT_COLOR = "#2036ff"; // app-blue-600

  function previewByLabel(label: string, mode: Mode = "auto") {
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    if (!v || !filtering) return;
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();
    const ids = resolveIds(label, mode);
    if (!ids.length) return;
    try {
      filtering.resetFilters?.();
    } catch {
      // intentional
    }
    try {
      // ghost=true keeps the rest faintly visible for context; the match renders normally on top.
      filtering.isolateObjects?.(ids, "dq-hover", true, true);
    } catch {
      // intentional
    }
    try {
      filtering.removeUserObjectColors?.();
      filtering.setUserObjectColors?.(combinedGroups([{ objectIds: ids, color: PREVIEW_HIGHLIGHT_COLOR }]));
    } catch {
      // intentional
    }
    v.requestRender?.();
  }

  function clearPreview() {
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    // Undo the hover isolation, then restore the committed overlay colours.
    try {
      filtering?.resetFilters?.();
    } catch {
      // intentional
    }
    try {
      filtering?.removeUserObjectColors?.();
      filtering?.setUserObjectColors?.(combinedGroups(lastColorGroups));
    } catch {
      // intentional
    }
    v?.requestRender?.();
  }

  // The model categories a given material/type label's elements belong to (usually one).
  function getLabelCategories(label: string, mode: Mode = "auto"): string[] {
    if (!indexesBuilt.value || categoryIndex.size === 0) buildIndexes();
    const ids = resolveIds(label, mode).map(String);
    if (!ids.length) return [];
    const out: string[] = [];
    for (const [key, set] of categoryIndex) {
      if (ids.some((id) => set.has(id))) out.push(categoryNames.get(key) ?? key);
    }
    return out.sort();
  }

  // The Revit families a given material/type label's elements belong to (the parent grouping
  // between category and the type itself).
  function getLabelFamilies(label: string, mode: Mode = "auto"): string[] {
    if (!indexesBuilt.value || familyIndex.size === 0) buildIndexes();
    const ids = resolveIds(label, mode).map(String);
    if (!ids.length) return [];
    const out: string[] = [];
    for (const [key, set] of familyIndex) {
      if (ids.some((id) => set.has(id))) out.push(familyNames.get(key) ?? key);
    }
    return out.sort();
  }

  // Diagnostic: how a label resolves against the two indexes. Lets a caller confirm the Type axis is
  // really showing building-element types (they should hit the type index, not the material index).
  function classifyLabel(label: string): { typeHits: number; materialHits: number } {
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();
    return {
      typeHits: getIdsForLabel(label, "type").length,
      materialHits: getIdsForLabel(label, "material").length,
    };
  }

  // Sequential, colourblind-safe heat ramp (YlOrRd): pale yellow -> deep red.
  // Encodes magnitude (not good/bad), so it avoids the red<->green trap.
  function heatColor(t: number): string {
    const c = Math.max(0, Math.min(1, t));
    const stops = [
      [255, 255, 178],
      [254, 178, 76],
      [253, 141, 60],
      [240, 59, 32],
      [189, 0, 38],
    ];
    const seg = c * (stops.length - 1);
    const i = Math.floor(seg);
    const f = seg - i;
    const a = stops[i];
    const b = stops[Math.min(i + 1, stops.length - 1)];
    const hex = (x: number) =>
      Math.round(a[x] + (b[x] - a[x]) * f)
        .toString(16)
        .padStart(2, "0");
    return `#${hex(0)}${hex(1)}${hex(2)}`;
  }

  // Log-normalized so one large emitter doesn't wash out the ramp. `range`, if narrower than the
  // full extent, ghosts out-of-range types, but hue stays normalized over the full set so it never shifts.
  function applyEmissionHeatmap(
    entries: { label: string; value: number }[],
    range?: { min: number; max: number },
    mode: Mode = "type"
  ) {
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    if (!v || !filtering || !entries?.length) return;
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();

    // Start clean so re-applying (e.g. "Show all" after a per-type highlight) un-ghosts the model and
    // drops the stale zoom target — letting recenterView fit the whole model again.
    try {
      filtering.resetFilters?.();
    } catch {
      // intentional
    }
    lastMatchedIds.value = [];

    const positive = entries.filter((e) => Number.isFinite(e.value) && e.value > 0);
    if (!positive.length) {
      reapplyColors();
      return;
    }

    // Normalize colors over the FULL entry set regardless of the active range.
    const logs = positive.map((e) => Math.log(e.value));
    const lmin = Math.min(...logs);
    const span = Math.max(...logs) - lmin || 1;

    // Determine which entries are inside the requested range (or all, when no range is given).
    const inRange = range ? positive.filter((e) => e.value >= range.min && e.value <= range.max) : positive;

    // Ghost everything but the painted types so buried ones (floors) show; an empty set would ghost the lot, so skip it.
    const inIds = inRange.flatMap((entry) => resolveIds(entry.label, mode));
    if (inIds.length) {
      try {
        filtering.isolateObjects?.(inIds, "emission-range", true, true);
      } catch {
        // intentional
      }
    }

    const groups: { objectIds: string[]; color: string }[] = [];
    for (const e of inRange) {
      const ids = resolveIds(e.label, mode);
      if (!ids.length) continue;
      const t = (Math.log(e.value) - lmin) / span;
      groups.push({ objectIds: ids, color: heatColor(t) });
    }
    if (!groups.length) {
      reapplyColors();
      return;
    }
    // Heatmap colours win over the excluded grey, the same as a picked type.
    applyUserColors(
      groups,
      groups.flatMap((group) => group.objectIds)
    );
    v.requestRender?.();
  }

  function clearEmissionHeatmap() {
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    filtering?.removeUserObjectColors();
    lastColorGroups = [];
    focusedObjectIds = new Set();
    // Undo any ghost isolation applied by emphasizeByLabel so the full model returns.
    try {
      filtering?.resetFilters?.();
    } catch {
      // intentional
    }
    try {
      getSelecting()?.clearSelection?.();
    } catch {
      // intentional
    }
    reapplyColors();
    // Drop the cached match so a subsequent recenter fits the whole model, not the last type.
    lastMatchedIds.value = [];
    v?.requestRender?.();
  }

  // Ghosts everything else so matched elements stay visible even when buried in the model,
  // then outlines and flies to them; resetFilters() (via clearEmissionHeatmap) restores the full model.
  function emphasizeByLabel(label: string, mode: Mode = "type") {
    const v = core.viewer;
    if (!v) return;
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();
    const ids = resolveIds(label, mode);
    if (!ids.length) return;
    lastMatchedIds.value = ids;

    const filtering = core.filteringRef?.value ?? core.filteringRef;
    const sel = getSelecting();

    // Replace any prior emphasis — picking a new type must not stack on the previous one.
    // isolateObjects() accumulates and selectObjects() doesn't auto-clear, so reset both first.
    try {
      sel?.clearSelection?.();
    } catch {
      // intentional
    }
    try {
      filtering?.resetFilters?.();
    } catch {
      // intentional
    }

    // ghost=true keeps the rest faintly visible for context while the match renders normally —
    // without this, a type buried inside the model is hidden behind the surrounding geometry.
    try {
      filtering?.isolateObjects?.(ids, "emission-type", true, true);
    } catch {
      // intentional: fall back to plain selection if isolation is unavailable
    }
    reapplyColors();

    try {
      sel?.selectObjects?.(ids, false);
    } catch {
      // intentional
    }
    zoomToIds(ids);
    v.requestRender?.();
  }

  // Paints matched elements in the same log-normalized YlOrRd ramp the legend/heatmap use; cleared by clearEmissionHeatmap.
  // `zoom=false` avoids a camera jump when the caller is only refreshing an already-focused type's hue.
  function highlightTypeByEmission(label: string, entries: { label: string; value: number }[], zoom = true, mode: Mode = "type") {
    const v = core.viewer;
    if (!v) return;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    if (!filtering) return;
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();
    const ids = resolveIds(label, mode);
    if (!ids.length) return;
    lastMatchedIds.value = ids;

    // Replace any prior highlight so switching types doesn't stack.
    const sel = getSelecting();
    try {
      sel?.clearSelection?.();
    } catch {
      // intentional
    }
    try {
      filtering.removeUserObjectColors?.();
    } catch {
      // intentional
    }
    try {
      filtering.resetFilters?.();
    } catch {
      // intentional
    }

    // Ghost everything else so the picked type stands out even when buried deep in the model.
    try {
      filtering.isolateObjects?.(ids, "emission-type", true, true);
    } catch {
      // intentional
    }

    // Paint the picked type with its emission-intensity colour (log-normalized like applyEmissionHeatmap).
    const positive = (entries ?? []).filter((entry) => Number.isFinite(entry.value) && entry.value > 0);
    const picked = positive.find((entry) => entry.label === label);
    if (picked) {
      const logs = positive.map((entry) => Math.log(entry.value));
      const lmin = Math.min(...logs);
      const span = Math.max(...logs) - lmin || 1;
      const t = (Math.log(picked.value) - lmin) / span;
      // Focused, so the picked type keeps its heat colour on every later repaint even where it is excluded.
      applyUserColors([{ objectIds: ids, color: heatColor(t) }], ids);
    } else {
      reapplyColors();
    }

    if (zoom) zoomToIds(ids);
    v.requestRender?.();
  }

  // Colours by tier (green=complete, yellow=needsInput, red=unmapped); worst-tier-wins on overlap.
  // `inRangeLabels`, when set, paints only those labels and ghosts the rest to keep colors readable.
  const DATA_QUALITY_COMPLETE_COLOR = "#6aae34"; // app-green-500
  const DATA_QUALITY_NEEDS_INPUT_COLOR = "#ef9f11"; // app-yellow-500
  const DATA_QUALITY_UNMAPPED_COLOR = "#ee4745"; // app-red-500

  function applyDataQuality(
    types: { label: string; tier: "complete" | "needsInput" | "unmapped" }[],
    mode: Mode = "auto",
    inRangeLabels?: Set<string>,
    // Type identifiers with no active material layer — tallied separately so an excluded type is
    // counted rather than dropped, without disturbing "worst tier wins" for the tiers that still matter.
    excludedLabels: string[] = [],
    options: { paint?: boolean } = {}
  ) {
    const { paint = true } = options;
    const empty = {
      complete: 0,
      needsInput: 0,
      unmapped: 0,
      excluded: 0,
      unresolved: 0,
      types: [] as { label: string; tier: "complete" | "needsInput" | "unmapped"; count: number }[],
      excludedTypes: [] as { label: string; count: number }[],
      idsByLabel: {} as Record<string, string[]>,
    };
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    if (!v || !filtering) return empty;
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();

    // Drop any prior per-type focus so re-applying shows the whole model again.
    if (paint) {
      try {
        filtering.resetFilters?.();
      } catch {
        // intentional
      }
      lastMatchedIds.value = [];
    }

    // Tier priority: unmapped (2) > needsInput (1) > complete (0). Worst tier wins on overlap.
    const tierPriority: Record<"complete" | "needsInput" | "unmapped", number> = {
      complete: 0,
      needsInput: 1,
      unmapped: 2,
    };

    // When a range is active, only paint in-range types; collect out-of-range ids for ghosting.
    const paintTypes = inRangeLabels ? types.filter((type) => inRangeLabels.has(type.label)) : types;
    const ghostTypes = inRangeLabels ? types.filter((type) => !inRangeLabels.has(type.label)) : [];

    // COUNTING retries in auto since strict lookups miss byType-tree labels; PAINTING stays strict
    // to avoid bleeding one type's colour into another. Unresolved in both is reported, not dropped.
    type Tier = "complete" | "needsInput" | "unmapped";
    const idTierMap = new Map<string, Tier>(); // painted
    const countTierMap = new Map<string, Tier>(); // counted: the bar, the legend, the lists
    const breakdown: { label: string; tier: Tier; count: number }[] = [];
    const idsByLabel: Record<string, string[]> = {};
    let unresolved = 0;

    const assignTier = (map: Map<string, Tier>, ids: string[], tier: Tier) => {
      for (const id of ids) {
        const existing = map.get(id);
        if (existing === undefined || tierPriority[tier] > tierPriority[existing]) map.set(id, tier);
      }
    };
    const resolveBoth = (label: string) => {
      const strictIds = getIdsForLabel(label, mode);
      return { strictIds, ids: strictIds.length ? strictIds : resolveIds(label, mode) };
    };

    for (const type of paintTypes) {
      const { strictIds, ids } = resolveBoth(type.label);
      if (!ids.length) {
        unresolved += 1;
        continue;
      }
      breakdown.push({ label: type.label, tier: type.tier, count: ids.length });
      idsByLabel[type.label] = ids;
      assignTier(countTierMap, ids, type.tier);
      if (strictIds.length) assignTier(idTierMap, strictIds, type.tier);
    }

    // Also add out-of-range types to the breakdown (for stats) but not to the paint map.
    for (const type of ghostTypes) {
      const { ids } = resolveBoth(type.label);
      if (!ids.length) {
        unresolved += 1;
        continue;
      }
      breakdown.push({ label: type.label, tier: type.tier, count: ids.length });
      idsByLabel[type.label] = ids;
    }

    const completeIds = new Set<string>();
    const needsInputIds = new Set<string>();
    const unmappedIds = new Set<string>();

    for (const [id, tier] of countTierMap) {
      if (tier === "unmapped") unmappedIds.add(id);
      else if (tier === "needsInput") needsInputIds.add(id);
      else completeIds.add(id);
    }

    // When a range is active, ghost out-of-range elements so the mapping-status colors on
    // in-range elements remain readable in the model.
    if (paint && ghostTypes.length) {
      const inIds = [...idTierMap.keys()];
      if (inIds.length) {
        try {
          filtering.isolateObjects?.(inIds, "emission-range-dq", true, true);
        } catch {
          // intentional
        }
      }
    }

    // Paint from the strict set only (see above).
    const paintIds: Record<Tier, string[]> = { complete: [], needsInput: [], unmapped: [] };
    for (const [id, tier] of idTierMap) paintIds[tier].push(id);
    const groups: { objectIds: string[]; color: string }[] = [];
    if (paintIds.complete.length) groups.push({ objectIds: paintIds.complete, color: DATA_QUALITY_COMPLETE_COLOR });
    if (paintIds.needsInput.length) groups.push({ objectIds: paintIds.needsInput, color: DATA_QUALITY_NEEDS_INPUT_COLOR });
    if (paintIds.unmapped.length) groups.push({ objectIds: paintIds.unmapped, color: DATA_QUALITY_UNMAPPED_COLOR });
    if (paint) {
      if (groups.length) {
        applyUserColors(groups);
        v.requestRender?.();
      } else {
        reapplyColors();
      }
    }

    // Tallied for the count/list only (painting is the persistent overlay's job). A label present in
    // both lists keeps only its scored tier — skipped here — so the two buckets stay a real partition.
    const tieredLabels = new Set(types.map((type) => type.label));
    const excludedIds = new Set<string>();
    const excludedTypes: { label: string; count: number }[] = [];
    for (const label of excludedLabels) {
      if (tieredLabels.has(label)) continue;
      const { ids } = resolveBoth(label);
      if (!ids.length) {
        unresolved += 1;
        continue;
      }
      idsByLabel[label] = ids;
      excludedTypes.push({ label, count: ids.length });
      ids.forEach((id) => excludedIds.add(id));
    }

    return {
      complete: completeIds.size,
      needsInput: needsInputIds.size,
      unmapped: unmappedIds.size,
      excluded: excludedIds.size,
      unresolved,
      types: breakdown,
      excludedTypes,
      idsByLabel,
    };
  }

  // Isolate one type by data-quality tier (ghost the rest), tint it with its tier colour, and zoom in.
  function focusDataQualityType(
    label: string,
    tier: "complete" | "needsInput" | "unmapped" | "excluded",
    mode: Mode = "auto",
    zoom = true
  ) {
    const v = core.viewer;
    if (!v) return;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();
    const ids = resolveIds(label, mode);
    if (!ids.length) return;
    lastMatchedIds.value = ids;
    try {
      getSelecting()?.clearSelection?.();
    } catch {
      // intentional
    }
    try {
      filtering?.resetFilters?.();
    } catch {
      // intentional
    }
    try {
      filtering?.isolateObjects?.(ids, "data-quality", true, true);
    } catch {
      // intentional
    }
    const tierColor =
      tier === "unmapped"
        ? DATA_QUALITY_UNMAPPED_COLOR
        : tier === "needsInput"
          ? DATA_QUALITY_NEEDS_INPUT_COLOR
          : tier === "excluded"
            ? EXCLUDED_GREY
            : DATA_QUALITY_COMPLETE_COLOR;
    try {
      applyUserColors([{ objectIds: ids, color: tierColor }]);
    } catch {
      // intentional
    }
    if (zoom) zoomToIds(ids);
    v.requestRender?.();
  }

  // Isolates a group of types at once, tinting each included id by its tier (worst-tier-wins where ids
  // overlap); `hideRest` hides everything else outright instead of ghosting it.
  function focusDataQualityGroup(
    types: { label: string; tier: "complete" | "needsInput" | "unmapped" }[],
    mode: Mode = "auto",
    zoom = false,
    hideRest = false
  ) {
    const v = core.viewer;
    if (!v) return;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();

    const tierPriority: Record<"complete" | "needsInput" | "unmapped", number> = {
      complete: 0,
      needsInput: 1,
      unmapped: 2,
    };
    const idTierMap = new Map<string, "complete" | "needsInput" | "unmapped">();
    for (const type of types ?? []) {
      const ids = resolveIds(type.label, mode);
      for (const id of ids) {
        const existing = idTierMap.get(id);
        if (existing === undefined || tierPriority[type.tier] > tierPriority[existing]) idTierMap.set(id, type.tier);
      }
    }
    const allIds = [...idTierMap.keys()];

    // With nothing to show, the ghost path leaves the model alone; the hide path empties the scene on purpose.
    if (!allIds.length && !hideRest) return;
    lastMatchedIds.value = allIds;

    try {
      getSelecting()?.clearSelection?.();
    } catch {
      // intentional
    }
    try {
      filtering?.resetFilters?.();
    } catch {
      // intentional
    }
    try {
      // An empty isolate shows everything, so an empty shown set hides each loaded model root instead.
      if (hideRest && !allIds.length) filtering?.hideObjects?.([...(core.loaded ?? [])], "data-quality", true, false);
      else filtering?.isolateObjects?.(allIds, "data-quality", true, !hideRest);
    } catch (error) {
      Sentry.captureException(error);
    }

    const completeIds: string[] = [];
    const needsInputIds: string[] = [];
    const unmappedIds: string[] = [];
    for (const [id, tier] of idTierMap) {
      if (tier === "unmapped") unmappedIds.push(id);
      else if (tier === "needsInput") needsInputIds.push(id);
      else completeIds.push(id);
    }
    const groups: { objectIds: string[]; color: string }[] = [];
    if (completeIds.length) groups.push({ objectIds: completeIds, color: DATA_QUALITY_COMPLETE_COLOR });
    if (needsInputIds.length) groups.push({ objectIds: needsInputIds, color: DATA_QUALITY_NEEDS_INPUT_COLOR });
    if (unmappedIds.length) groups.push({ objectIds: unmappedIds, color: DATA_QUALITY_UNMAPPED_COLOR });
    // An empty groups list is deliberate: when nothing shown resolves, no visible element carries a tier colour.
    try {
      applyUserColors(groups);
    } catch {
      // intentional
    }

    if (zoom) zoomToIds(allIds);
    v.requestRender?.();
  }

  // Paints each label in its category's colour, the key's `combine` deciding where ids overlap. `shown` keeps only the
  // folded categories it names and hides the rest; `labels` focuses those types and ghosts the rest; neither ghosts the unpainted.
  function applyCategoryColours(
    entries: { label: string; category: string }[],
    combine: (first: string, second: string) => string,
    colours: Record<string, string>,
    options: { shown?: Set<string> | null; labels?: Set<string> | null; mode?: Mode } = {}
  ) {
    const { shown = null, labels = null, mode = "type" } = options;
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    if (!v || !filtering) return;
    if (!indexesBuilt.value || (materialIndex.size === 0 && typeIndex.size === 0)) buildIndexes();

    const categoryById = new Map<string, string>();
    for (const entry of entries ?? []) {
      if (labels && !labels.has(entry.label)) continue;
      for (const id of resolveIds(entry.label, mode)) {
        const existing = categoryById.get(id);
        categoryById.set(id, existing === undefined ? entry.category : combine(existing, entry.category));
      }
    }
    if (shown && !labels) for (const [id, category] of categoryById) if (!shown.has(category)) categoryById.delete(id);
    const allIds = [...categoryById.keys()];
    // A subset is what recenterView fits to afterwards; the overview fits the whole model.
    lastMatchedIds.value = shown || labels ? allIds : [];

    try {
      getSelecting()?.clearSelection?.();
    } catch {
      // intentional
    }
    try {
      filtering.resetFilters?.();
    } catch {
      // intentional
    }
    try {
      // An empty isolate shows everything, so an empty chip hides each loaded model root instead.
      if (shown && !labels && !allIds.length) filtering.hideObjects?.([...(core.loaded ?? [])], "category", true, false);
      else if (allIds.length) filtering.isolateObjects?.(allIds, "category", true, !shown || !!labels);
    } catch (error) {
      Sentry.captureException(error);
    }

    const idsByCategory = new Map<string, string[]>();
    for (const [id, category] of categoryById) idsByCategory.set(category, [...(idsByCategory.get(category) ?? []), id]);
    const groups = [...idsByCategory.entries()]
      .filter(([category]) => colours[category])
      .map(([category, objectIds]) => ({ objectIds, color: colours[category] }));
    if (groups.length) applyUserColors(groups, allIds);
    else {
      // Nothing left to paint must remove the previous colours outright, as reapplyColors does for an empty set.
      lastColorGroups = [];
      focusedObjectIds = new Set();
      reapplyColors();
    }
    v.requestRender?.();
  }

  function clearDataQuality() {
    const v = core.viewer;
    const filtering = core.filteringRef?.value ?? core.filteringRef;
    filtering?.removeUserObjectColors?.();
    lastColorGroups = [];
    focusedObjectIds = new Set();
    try {
      filtering?.resetFilters?.();
    } catch {
      // intentional
    }
    reapplyColors();
    lastMatchedIds.value = [];
    v?.requestRender?.();
  }

  return {
    // indices
    buildIndexes,
    invalidate,
    hasRenderableIds,
    labelResolvesInModel,

    // actions
    clearFilters,
    applyEmissionHeatmap,
    clearEmissionHeatmap,
    emphasizeByLabel,
    highlightTypeByEmission,
    applyDataQuality,
    focusDataQualityType,
    focusDataQualityGroup,
    clearDataQuality,
    applyCategoryColours,
    selectObjectIds,
    isolateObjectIds,
    clearInspectIsolation,
    objectIdsForRawType,
    zoomToIds,
    reapplyColors,
    previewByLabel,
    clearPreview,
    getLabelCategories,
    getLabelFamilies,
    classifyLabel,
    loadExcludedLayers,
    setExcludedPairs,
    setExcludedForPair,

    // state
    emissionRange,
    excludedObjectIds,

    // setters
    setEmissionRange,

    // computed / helpers
    getZoomTargets,
  };
});
